import React from 'react';

import styled from '@emotion/styled';

import { CardElement } from '@stripe/react-stripe-js';
import { StripeElementChangeEvent } from '@stripe/stripe-js';

import {
  COLORS,
  Text as CleanText,
  FontWeight,
  Input,
  Label,
} from '@clutter/clean';

import {
  PAYMENT_ERROR_TO_MESSAGE,
  PaymentMethodErrorKind,
} from '@shared/payment_method_error';

const CC_INPUT_BREAKPOINT = 340;

const getStripeElementStyle = (isMobile: boolean, challenger: boolean) => ({
  base: {
    fontSize: isMobile ? '13px' : '16px',
    fontFamily: challenger
      ? '"Avenir Next", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      : 'Basier Square',
    lineHeight: challenger ? '24px' : '32px',
    fontSmoothing: 'antialiased',
    color: challenger ? '#040711' : undefined,
    '::placeholder': {
      color: challenger ? '#66686e' : COLORS.hippo,
    },
  },
});

const Container = styled.div<{ error?: boolean }>`
  .StripeElement {
    line-height: 32px;
    border: 1.76px solid #ebeced;
    padding: 8px 16px;
    border-radius: 4px;
    border-color: ${({ error }) => (error ? COLORS.toucan : COLORS.grayBorder)};
    background: ${COLORS.cloud};
  }
`;

/** Figma 707:5595 — 56px fields, 8px radius, Avenir 16/24. */
const ChallengerContainer = styled.div<{ error?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  .StripeElement {
    width: 100%;
    height: 56px;
    box-sizing: border-box;
    padding: 16px;
    border: 1px solid
      ${({ error }) => (error ? COLORS.toucan : 'rgba(154, 161, 165, 0.3)')};
    border-radius: 8px;
    background: #ffffff;
    line-height: 24px;
  }
`;

const ChallengerNameInput = styled.input`
  appearance: none;
  width: 100%;
  height: 56px;
  padding: 12px 16px;
  box-sizing: border-box;
  border: 1px solid rgba(154, 161, 165, 0.3);
  border-radius: 8px;
  background: #ffffff;
  font-family:
    'Avenir Next',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-size: 16px;
  font-weight: ${FontWeight.Regular};
  line-height: 24px;
  color: #040711;

  &::placeholder {
    color: #66686e;
  }

  &:focus {
    outline: none;
    border-color: #0b7a7a;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;
  font-size: 16px;
  @media (max-width: ${CC_INPUT_BREAKPOINT}px) {
    font-size: 13px;
  }
`;

const Error = styled(CleanText.Caption)`
  height: 28px;
  padding: 8px 0 0;
`;

const ChallengerError = styled(CleanText.Caption)`
  padding: 0;
  min-height: 0;
`;

type CreditCardInput = {
  billingName?: string;
  disabled?: boolean;
  billingNameError?: string;
  paymentError?: PaymentMethodErrorKind;
  appearance?: 'classic' | 'challenger';
  onChangeBillingName(billingName: string): void;
  onChangeCard(changeEvent: StripeElementChangeEvent): void;
};

export const CreditCardInput: React.FC<CreditCardInput> = ({
  billingName,
  disabled = false,
  onChangeBillingName,
  onChangeCard,
  paymentError,
  billingNameError,
  appearance = 'classic',
}) => {
  const isMobile = window.innerWidth < CC_INPUT_BREAKPOINT;
  const challenger = appearance === 'challenger';

  if (challenger) {
    return (
      <ChallengerContainer error={!!paymentError}>
        <ChallengerNameInput
          id="billingName"
          onChange={(e) => onChangeBillingName(e.target.value)}
          type="text"
          value={billingName ?? ''}
          disabled={disabled}
          autoComplete="chrome-off"
          placeholder="Name on the card"
          aria-label="Name on the card"
          aria-invalid={!!billingNameError}
        />
        {billingNameError && (
          <ChallengerError color={COLORS.toucan} weight={FontWeight.Medium}>
            {billingNameError}
          </ChallengerError>
        )}
        <CardElement
          onChange={onChangeCard}
          options={{ style: getStripeElementStyle(isMobile, true) }}
        />
        {paymentError && (
          <ChallengerError color={COLORS.toucan} weight={FontWeight.Medium}>
            {PAYMENT_ERROR_TO_MESSAGE[paymentError]}
          </ChallengerError>
        )}
      </ChallengerContainer>
    );
  }

  return (
    <Container error={!!paymentError}>
      <Label htmlFor="billingName">Name on card</Label>
      <StyledInput
        id="billingName"
        onChange={(e) => onChangeBillingName(e.target.value)}
        type="text"
        value={billingName ?? ''}
        disabled={disabled}
        autoComplete="chrome-off"
        state={billingNameError ? 'error' : undefined}
      />
      <Error color={COLORS.toucan} weight={FontWeight.Medium}>
        {billingNameError}
      </Error>
      <Label>Card number</Label>
      <CardElement
        onChange={onChangeCard}
        options={{ style: getStripeElementStyle(isMobile, false) }}
      />
      <Error color={COLORS.toucan} weight={FontWeight.Medium}>
        {paymentError ? PAYMENT_ERROR_TO_MESSAGE[paymentError] : ''}
      </Error>
    </Container>
  );
};
